import { Controller } from "@hotwired/stimulus"

// A show's episode list: whole-catalog search, "show older" paging, and the
// sticky control bar that keeps both reachable however far you have
// scrolled.
//
// Search hits the server rather than filtering the rendered rows, because
// the rendered rows are one page of what may be thousands. Filtering only
// what is on screen looks broken the moment you search for something older
// than the page (Derick, 2026-08-28).
export default class extends Controller {
  static targets = ["input", "results", "spinner", "bar", "sentinel"]
  static values = { url: String }

  connect() {
    this.timer = null
    this.seq = 0
    this.#measureMasthead()
    this.onResize = () => this.#measureMasthead()
    window.addEventListener("resize", this.onResize)

    // The bar condenses (and grows a title) once the hero has scrolled off.
    // A scroll listener rather than an IntersectionObserver: the comparison
    // is against the masthead's height, not the viewport's, so it is right
    // even where the viewport is an odd size.
    this.onScroll = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        if (!this.hasSentinelTarget || !this.hasBarTarget) return
        const top = this.sentinelTarget.getBoundingClientRect().top
        this.barTarget.classList.toggle("stuck", top < this.mastheadHeight)
      })
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.onScroll()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    if (this.frame) cancelAnimationFrame(this.frame)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onResize)
  }

  search() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.#load({ q: this.inputTarget.value.trim() }), 200)
  }

  more(event) {
    const page = event.params.page
    this.#load({ q: this.inputTarget.value.trim(), page }, { append: true, button: event.currentTarget })
  }

  async #load(params, { append = false, button = null } = {}) {
    const mine = ++this.seq
    if (this.hasSpinnerTarget && !append) this.spinnerTarget.hidden = false
    if (button) { button.disabled = true; button.textContent = "Loading…" }

    const url = new URL(this.urlValue, window.location.origin)
    if (params.q) url.searchParams.set("q", params.q)
    if (params.page) url.searchParams.set("page", params.page)

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(response.status)
      const html = await response.text()
      if (mine !== this.seq) return // a newer keystroke already won
      if (append) {
        // Keep the rows already on screen; swap in the new page's rows and
        // its own count/Older button, so the position never jumps.
        const fragment = document.createElement("div")
        fragment.innerHTML = html
        const rows = fragment.querySelector(".episode-list")
        const existing = this.resultsTarget.querySelector(".episode-list")
        if (rows && existing) existing.append(...rows.children)
        this.resultsTarget.querySelector(".episode-more")?.remove()
        const nextButton = fragment.querySelector(".episode-more")
        if (nextButton) this.resultsTarget.append(nextButton)
        const count = fragment.querySelector(".episode-count")
        const shown = existing ? existing.children.length : 0
        const currentCount = this.resultsTarget.querySelector(".episode-count")
        if (count && currentCount) {
          currentCount.textContent = count.textContent.replace(/Showing \d+/, `Showing ${shown}`)
        }
      } else {
        this.resultsTarget.innerHTML = html
      }
    } catch (_error) {
      if (button) { button.disabled = false; button.textContent = "Show older episodes" }
    } finally {
      if (this.hasSpinnerTarget && !append) this.spinnerTarget.hidden = true
    }
  }

  #measureMasthead() {
    const masthead = document.querySelector(".masthead")
    this.mastheadHeight = masthead ? Math.round(masthead.getBoundingClientRect().height) : 0
    document.documentElement.style.setProperty("--masthead-h", `${this.mastheadHeight}px`)
  }
}
